import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Users,
  Search,
  Filter,
  MoreHorizontal,
  UserCheck,
  UserX,
  Mail,
  Shield,
  CreditCard,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trash2,
  UserPlus,
  Package,
  Key,
  Lock
} from 'lucide-react'
import { adminApi } from '@/lib/api'
import { notify } from '@/lib/notify'

interface User {
  _id: string
  name: string
  email: string
  role: string
  emailVerifiedAt?: string
  isBanned: boolean
  hasActiveSubscription?: boolean
  subscriptionStatus?: string
  createdAt: string
  lastLoginAt?: string
  stats: {
    totalSMS: number
    monthlySMS: number
    devices: number
    apiKeys: number
    supportTickets: number
  }
  activeSubscription?: {
    _id: string
    plan: {
      _id: string
      name: string
      description: string
      price: number
      currency: string
      recurringInterval: string
      dailyLimit: number
      monthlyLimit: number
      maxDevices: number
      maxApiKeys: number
    }
    status: string
    isActive: boolean
  }
}

export default function UserManagement() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [planFilter, setPlanFilter] = useState('all')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [assignPlanDialogOpen, setAssignPlanDialogOpen] = useState(false)
  const [selectedUserForPlan, setSelectedUserForPlan] = useState<User | null>(null)
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'REGULAR',
    emailVerified: false
  })
  const queryClient = useQueryClient()

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['admin-users', page, search, roleFilter, statusFilter, planFilter],
    queryFn: () => adminApi.get('/admin/users', {
      params: {
        page,
        limit: 20,
        search,
        role: roleFilter === 'all' ? '' : roleFilter,
        status: statusFilter === 'all' ? '' : statusFilter,
        plan: planFilter === 'all' ? '' : planFilter
      }
    }).then((res) => res.data.data),
    refetchInterval: 30000,
  })

  const { data: plansData } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => adminApi.get('/admin/plans').then((res) => res.data.data?.plans || []),
  })

  const bypassEmailMutation = useMutation({
    mutationFn: (userId: string) => 
      adminApi.post(`/admin/users/${userId}/bypass-email-verification`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    }
  })

  const updateBanStatusMutation = useMutation({
    mutationFn: ({ userId, isBanned }: { userId: string; isBanned: boolean }) => 
      adminApi.patch(`/admin/users/${userId}/ban`, { isBanned }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    }
  })

  const overrideSubscriptionMutation = useMutation({
    mutationFn: ({ userId, hasActiveSubscription, subscriptionStatus }: {
      userId: string
      hasActiveSubscription: boolean
      subscriptionStatus: string
    }) => adminApi.put(`/admin/users/${userId}/subscription-override`, {
      hasActiveSubscription,
      subscriptionStatus
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    }
  })

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => adminApi.delete(`/admin/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    }
  })

  const createUserMutation = useMutation({
    mutationFn: (userData: typeof newUser) => adminApi.post('/admin/users', userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setCreateDialogOpen(false)
      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'REGULAR',
        emailVerified: false
      })
    }
  })

  const assignPlanMutation = useMutation({
    mutationFn: ({ userId, planId }: { userId: string; planId: string }) =>
      adminApi.post(`/admin/users/${userId}/assign-plan`, { planId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setAssignPlanDialogOpen(false)
      setSelectedUserForPlan(null)
      setSelectedPlanId('')
    }
  })

  const updatePasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      adminApi.patch(`/admin/users/${userId}/password`, { newPassword }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setPasswordDialogOpen(false)
      setSelectedUserForPassword(null)
      setNewPassword('')
      // Show success message
      notify.success('Password updated')
    },
    onError: (error) => {
      console.error('Failed to update password:', error)
      notify.error("Couldn't update the password", 'Try again.')
    }
  })

  const handleBypassEmail = (userId: string) => {
    bypassEmailMutation.mutate(userId)
  }

  const handleBanToggle = (user: User) => {
    updateBanStatusMutation.mutate({
      userId: user._id,
      isBanned: !user.isBanned
    })
  }

  const handleSubscriptionAction = (userId: string, action: string) => {
    const actionConfig = {
      active: { hasActiveSubscription: true, subscriptionStatus: 'active' },
      pause: { hasActiveSubscription: true, subscriptionStatus: 'paused' },
      cancel: { hasActiveSubscription: false, subscriptionStatus: 'cancelled' },
      none: { hasActiveSubscription: false, subscriptionStatus: 'none' }
    };
    
    const config = actionConfig[action as keyof typeof actionConfig];
    if (config) {
      overrideSubscriptionMutation.mutate({
        userId,
        ...config
      });
    }
  }

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteUser = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete._id)
    }
  }

  const handleCreateUser = () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      return
    }
    createUserMutation.mutate(newUser)
  }

  const handleAssignPlan = (user: User) => {
    setSelectedUserForPlan(user)
    setSelectedPlanId('')
    setAssignPlanDialogOpen(true)
  }

  const confirmAssignPlan = () => {
    if (!selectedUserForPlan || !selectedPlanId) {
      return
    }
    assignPlanMutation.mutate({
      userId: selectedUserForPlan._id,
      planId: selectedPlanId
    })
  }

  const handleUpdatePassword = (user: User) => {
    setSelectedUserForPassword(user)
    setNewPassword('')
    setPasswordDialogOpen(true)
  }

  const confirmUpdatePassword = () => {
    if (!selectedUserForPassword || !newPassword || newPassword.length < 6) {
      return
    }
    updatePasswordMutation.mutate({
      userId: selectedUserForPassword._id,
      newPassword
    })
  }

  const users = usersData?.users || []
  const pagination = usersData?.pagination || {}

  const getPlanInfo = (user: User) => {
    if (!user.activeSubscription) {
      return (
        <div className="flex items-center">
          <Badge variant="secondary" className="text-xs">
            No Plan
          </Badge>
        </div>
      )
    }

    const { plan } = user.activeSubscription
    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: plan.currency || 'USD'
    }).format(plan.price)

    return (
      <div className="space-y-1">
        <div className="font-medium text-gray-900 text-sm">{plan.name}</div>
        <div className="text-xs text-gray-500">
          {formattedPrice}/{plan.recurringInterval}
        </div>
      </div>
    )
  }

  const getDetailedUsage = (user: User) => {
    const { stats, activeSubscription } = user
    
    // Default limits for users without plans
    const defaultLimits = {
      monthlyLimit: 100, // Default free plan SMS limit
      maxDevices: 1,     // Default free plan device limit  
      maxApiKeys: 1      // Default free plan API key limit
    }
    
    const planLimits = activeSubscription?.plan || defaultLimits
    
    const formatUsageItem = (used: number, limit: number, label: string, color: string) => {
      const percentage = limit > 0 ? (used / limit) * 100 : 0
      const isOverLimit = used > limit && limit !== -1
      const displayLimit = limit === -1 ? '∞' : limit.toLocaleString()
      
      return (
        <div className="flex items-center justify-between text-xs">
          <span className={`font-medium ${isOverLimit ? 'text-red-600' : color}`}>
            {used.toLocaleString()}/{displayLimit} {label}
          </span>
          {limit !== -1 && (
            <div className={`w-12 h-1.5 rounded-full bg-gray-200 ml-2`}>
              <div 
                className={`h-1.5 rounded-full transition-all ${
                  isOverLimit ? 'bg-red-500' : 
                  percentage > 80 ? 'bg-amber-500' : 
                  'bg-green-500'
                }`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="space-y-2">
        {formatUsageItem(
          stats.monthlySMS, 
          planLimits.monthlyLimit || defaultLimits.monthlyLimit, 
          'SMS', 
          'text-blue-600'
        )}
        {formatUsageItem(
          stats.devices, 
          planLimits.maxDevices || defaultLimits.maxDevices, 
          'Devices', 
          'text-green-600'
        )}
        {formatUsageItem(
          stats.apiKeys, 
          planLimits.maxApiKeys || defaultLimits.maxApiKeys, 
          'API Keys', 
          'text-purple-600'
        )}
      </div>
    )
  }

  const getStatusBadge = (user: User) => {
    if (user.isBanned) {
      return <Badge variant="destructive" className="text-xs">Banned</Badge>
    }
    if (!user.emailVerifiedAt) {
      return <Badge variant="outline" className="text-xs">Unverified</Badge>
    }
    if (user.hasActiveSubscription) {
      return <Badge variant="default" className="text-xs">Premium</Badge>
    }
    return <Badge variant="secondary" className="text-xs">Active</Badge>
  }

  const getEmailStatus = (user: User) => {
    if (user.emailVerifiedAt) {
      return (
        <div className="flex items-center text-green-600">
          <CheckCircle2 className="h-4 w-4 mr-1" />
          <span className="text-xs">Verified</span>
        </div>
      )
    }
    return (
      <div className="flex items-center text-amber-600">
        <AlertCircle className="h-4 w-4 mr-1" />
        <span className="text-xs">Pending</span>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">
            Manage users, subscriptions, and email verification status
          </p>
        </div>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="REGULAR">Regular</SelectItem>
                <SelectItem value="ADMIN">Admin</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="banned">Banned</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="no-plan">No Plan</SelectItem>
                {plansData?.filter((plan: any) => plan.isActive).map((plan: any) => (
                  <SelectItem key={plan._id} value={plan._id}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Users ({pagination.total || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email Status</TableHead>
                    <TableHead>Current Plan</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user: User) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-xs font-medium text-blue-600">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center justify-between">
                          {getEmailStatus(user)}
                          {!user.emailVerifiedAt && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleBypassEmail(user._id)}
                              disabled={bypassEmailMutation.isPending}
                              className="ml-2 text-xs"
                            >
                              Bypass
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        {getPlanInfo(user)}
                      </TableCell>
                      
                      <TableCell>
                        {getDetailedUsage(user)}
                      </TableCell>
                      
                      <TableCell>
                        <div className="text-sm text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAssignPlan(user)}
                            disabled={assignPlanMutation.isPending}
                            className="text-blue-600 hover:text-blue-700 hover:border-blue-300"
                          >
                            <Package className="h-3 w-3 mr-1" />
                            Assign Plan
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdatePassword(user)}
                            disabled={updatePasswordMutation.isPending}
                            className="text-amber-600 hover:text-amber-700 hover:border-amber-300"
                            title="Update Password"
                          >
                            <Key className="h-3 w-3 mr-1" />
                            Update Pass
                          </Button>

                          <Button
                            size="sm"
                            variant={user.isBanned ? "default" : "destructive"}
                            onClick={() => handleBanToggle(user)}
                            disabled={updateBanStatusMutation.isPending}
                          >
                            {user.isBanned ? (
                              <>
                                <UserCheck className="h-3 w-3 mr-1" />
                                Unban
                              </>
                            ) : (
                              <>
                                <UserX className="h-3 w-3 mr-1" />
                                Ban
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteUser(user)}
                            disabled={deleteUserMutation.isPending}
                            className="text-red-600 hover:text-red-700 hover:border-red-300"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.pages} 
                    ({pagination.total} total users)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= pagination.pages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-red-600 font-medium flex items-center gap-2">
              <span>⚠️ This action is irreversible and will permanently delete:</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-2 ml-4">
              <li>• User account and profile</li>
              <li>• All SMS history and messages</li>
              <li>• Registered devices and API keys</li>
              <li>• Support tickets and access logs</li>
              <li>• Subscription and billing data</li>
            </ul>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteUser}
              disabled={deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending ? (
                <>
                  <div className="h-3 w-3 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-3 w-3 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system. You can set their role and email verification status.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter full name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">User Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(role) => setNewUser({ ...newUser, role })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REGULAR">Regular User</SelectItem>
                  <SelectItem value="ADMIN">Admin User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                id="emailVerified"
                type="checkbox"
                checked={newUser.emailVerified}
                onChange={(e) => 
                  setNewUser({ ...newUser, emailVerified: e.target.checked })
                }
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <Label 
                htmlFor="emailVerified"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Mark email as verified
              </Label>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={createUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={createUserMutation.isPending || !newUser.name || !newUser.email || !newUser.password}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {createUserMutation.isPending ? (
                <>
                  <div className="h-3 w-3 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="h-3 w-3 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Plan Dialog */}
      <Dialog open={assignPlanDialogOpen} onOpenChange={setAssignPlanDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Plan to User</DialogTitle>
            <DialogDescription>
              Select a subscription plan to assign to <strong>{selectedUserForPlan?.name}</strong> ({selectedUserForPlan?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="planSelect">Select Plan</Label>
              <Select
                value={selectedPlanId}
                onValueChange={setSelectedPlanId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan to assign..." />
                </SelectTrigger>
                <SelectContent>
                  {plansData?.filter((plan: any) => plan.isActive).map((plan: any) => (
                    <SelectItem key={plan._id} value={plan._id}>
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <div className="font-medium">{plan.name}</div>
                          <div className="text-sm text-gray-500">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: plan.currency || 'USD'
                            }).format(plan.price)} / {plan.recurringInterval}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 ml-4">
                          {plan.monthlyLimit === -1 ? 'Unlimited' : plan.monthlyLimit.toLocaleString()} SMS/mo
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedPlanId && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  {(() => {
                    const selectedPlan = plansData?.find((plan: any) => plan._id === selectedPlanId);
                    if (!selectedPlan) return null;
                    
                    return (
                      <div className="space-y-2">
                        <div className="font-medium text-blue-900">Plan Details:</div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Monthly SMS:</span>
                            <span>{selectedPlan.monthlyLimit === -1 ? 'Unlimited' : selectedPlan.monthlyLimit.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Daily SMS:</span>
                            <span>{selectedPlan.dailyLimit === -1 ? 'Unlimited' : selectedPlan.dailyLimit.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Max Devices:</span>
                            <span>{selectedPlan.maxDevices}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>API Keys:</span>
                            <span>{selectedPlan.maxApiKeys}</span>
                          </div>
                          {selectedPlan.features && selectedPlan.features.length > 0 && (
                            <div className="mt-2">
                              <div className="font-medium">Features:</div>
                              <ul className="text-xs text-gray-600 space-y-0.5 ml-2">
                                {selectedPlan.features.map((feature: string, index: number) => (
                                  <li key={index}>• {feature}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-amber-600">⚠️</span>
                </div>
                <div className="ml-2 text-sm text-amber-700">
                  <div className="font-medium">Important:</div>
                  <div>This will replace any existing active subscription for this user. The assignment will be effective immediately.</div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setAssignPlanDialogOpen(false)}
              disabled={assignPlanMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAssignPlan}
              disabled={assignPlanMutation.isPending || !selectedPlanId}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assignPlanMutation.isPending ? (
                <>
                  <div className="h-3 w-3 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Assigning...
                </>
              ) : (
                <>
                  <Package className="h-3 w-3 mr-2" />
                  Assign Plan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Update Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Update User Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{selectedUserForPassword?.name}</strong> ({selectedUserForPassword?.email}). This will allow them to login immediately with the new password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min. 6 characters)"
              />
              {newPassword && newPassword.length < 6 && (
                <div className="text-red-600 text-sm">
                  Password must be at least 6 characters long
                </div>
              )}
            </div>
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                <strong>Security Notice:</strong> This action will overwrite the user's current password.
                They will be able to login immediately with the new password.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPasswordDialogOpen(false)}
              disabled={updatePasswordMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmUpdatePassword}
              disabled={!newPassword || newPassword.length < 6 || updatePasswordMutation.isPending}
            >
              {updatePasswordMutation.isPending ? (
                <>
                  <div className="h-3 w-3 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Updating...
                </>
              ) : (
                <>
                  <Key className="h-3 w-3 mr-2" />
                  Update Password
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
